import { Fragment, useMemo } from 'react';
import cx from 'classnames';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import {
  PersonalPlanPeriodType,
  useGetPersonalPlan,
} from '@legacy/apis/personalPlanAPI/get';
import { useLocalStorage } from '@legacy/hooks/useLocalStorage';
import { getApiDateFormat } from '@legacy/utils/date/date';
import {
  addDays,
  addMonths,
  addWeeks,
  format,
  isSunday,
  isWeekend,
  lastDayOfMonth,
  lastDayOfWeek,
} from 'date-fns';
import { IResourcePlanner } from '@legacy/apis/types/resourcePlannerAPI';
import { getPersonalPlan } from '@legacy/apis/personalPlanAPI/get/getPersonalPlan';
import {
  Box,
  capitalize,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButtonGroup,
} from '@mui/material';
import {
  CalendarMonth,
  DateRange,
  KeyboardArrowDown,
  KeyboardArrowRight,
  Today,
} from '@mui/icons-material';
import grays from '@legacy/assets/styles/variables/colors/global/grays.module.scss';
import pink from '@legacy/assets/styles/variables/colors/themes/pink.module.scss';
import borderRadius from '@legacy/assets/styles/variables/border-radius.module.scss';
import typography from '@legacy/assets/styles/variables/typography.module.scss';
import { formatNumber } from '@legacy/utils/number';
import { Alert, ToggleButton } from '@legacy/components/mui-components';
import { useTranslation } from 'react-i18next';
import { DataAutomationId } from '@legacy/utils/ts-utils';
import {
  LOCAL_STORAGE_PERSONAL_PLAN_EXPANDED_ROWS_KEY,
  LOCAL_STORAGE_PERSONAL_PLAN_PERIOD_TYPE_KEY,
} from '../../constants';
import { PersonalPlanTableSkeleton } from '../PersonalPlanTableSkeleton';
import styles from '../PersonalPlanTable.module.scss';

type Task = {
  id: string;
  type: 'Employee' | 'Project' | 'Task';
};

export const PersonalPlanTable = ({ ...props }: DataAutomationId) => {
  const siteLocale = localStorage.getItem('userLanguage') ?? 'en-GB';
  const { t } = useTranslation('personalPlan');

  const queryClient = useQueryClient();
  const [periodType, setPeriodType] = useLocalStorage<PersonalPlanPeriodType>(
    LOCAL_STORAGE_PERSONAL_PLAN_PERIOD_TYPE_KEY,
    'day',
  );

  const today = useMemo(() => new Date(), []);
  const periodsToShow = 4; // Number of periods to show (it includes the current period = 5)
  const periodStartDate = getApiDateFormat(today);

  // Calculate end date based on periodType
  const periodEndDate = useMemo(() => {
    switch (periodType) {
      case 'day':
        return getApiDateFormat(addDays(today, 6));
      case 'week':
        return getApiDateFormat(lastDayOfWeek(addWeeks(today, periodsToShow)));
      case 'month':
        return getApiDateFormat(
          lastDayOfMonth(addMonths(today, periodsToShow)),
        );
      default:
        return getApiDateFormat(addDays(today, 6));
    }
  }, [periodType, today, periodsToShow]);

  const [expandedTasks, setExpandedTasks] = useLocalStorage<Task[]>(
    LOCAL_STORAGE_PERSONAL_PLAN_EXPANDED_ROWS_KEY,
    [],
  );

  // Fetch root tasks
  const {
    data: rootTasks,
    refetch,
    isLoading,
    isSuccess,
    isError,
  } = useGetPersonalPlan({
    periodTypes: periodType,
    periodStartDate,
    periodEndDate,
  });

  // Fetch child tasks for all expanded items using `useQueries`
  const childQueries = useQueries({
    queries: Array.from(expandedTasks).map(({ id, type }) => ({
      queryKey: ['tasks', id, periodType],
      queryFn: () =>
        getPersonalPlan({
          periodTypes: periodType,
          expandId: id,
          periodStartDate,
          periodEndDate,
          expandType: type,
        }),
      enabled: true,
    })),
  });

  const isEmpty = rootTasks?.model.properties.children?.length === 0;

  // Create a map from query results
  const childTasksMap = new Map<string, IResourcePlanner>();
  const isFetchingChildTasks = new Set<string>();

  childQueries.forEach((query, index) => {
    const { id } = Array.from(expandedTasks)[index];
    if (query.isFetching) {
      isFetchingChildTasks.add(id);
    }

    if (query.data) {
      childTasksMap.set(id, query.data.model.properties || []);
    }
  });

  // Expand/collapse tasks
  const toggleExpand = (task: Task) => {
    setExpandedTasks((prev) => {
      const taskExists = prev.some(
        (expandedTask) => expandedTask.id === task.id,
      );

      if (taskExists) {
        // Remove the task if it exists
        return prev.filter((expandedTask) => expandedTask.id !== task.id);
      }
      // Add the task if it doesn't exist
      return [...prev, task];
    });
  };

  // Handle view mode change
  const handleViewChange = (newPeriodType: PersonalPlanPeriodType) => {
    setPeriodType(newPeriodType);
    refetch(); // Fetch new root-level data

    // Re-fetch expanded tasks to update based on new view mode
    expandedTasks.forEach(({ id }) => {
      queryClient.invalidateQueries({
        queryKey: ['tasks', id, newPeriodType],
      });
    });
  };

  // Recursive table row rendering with cycle detection
  const renderTasks = (
    items: IResourcePlanner,
    level = 0,
    visitedIds = new Set<string>(),
  ) => {
    const { children: tasks, periods } = items;
    return tasks?.map((task) => {
      const { workItemSourceReferenceId: taskId, type } = task;

      if (visitedIds.has(taskId)) {
        // Guard against cycles
        return null;
      }

      const newVisitedIds = new Set(visitedIds).add(taskId);
      const childTasks = childTasksMap.get(taskId);
      const isFetching = isFetchingChildTasks.has(taskId);
      const isExpanded = expandedTasks.some(
        (expandedTask) => expandedTask.id === taskId,
      );

      return (
        <Fragment key={taskId}>
          <TableRow>
            <TableCell colSpan={2}>
              <Stack
                direction="row"
                gap={1}
                sx={{ pl: level * 4 }}
                alignItems="center"
              >
                {isExpanded && isFetching && (
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <CircularProgress size={20} />
                  </Box>
                )}
                {task.canExpand && !isFetching && (
                  <IconButton
                    title={
                      isExpanded
                        ? `${t('CollapseRowButton')}, ${task.name}`
                        : `${t('ExpandRowButton')}, ${task.name}`
                    }
                    onClick={() => toggleExpand({ id: taskId, type })}
                    data-automation-id={
                      props['data-automation-id'] + 'RowExpandButton'
                    }
                    size="small"
                    aria-expanded={isExpanded}
                  >
                    {isExpanded ? (
                      <KeyboardArrowDown fontSize="small" />
                    ) : (
                      <KeyboardArrowRight fontSize="small" />
                    )}
                  </IconButton>
                )}
                {!task.canExpand && <div style={{ width: 34, height: 30 }} />}
                {task.name}
              </Stack>
            </TableCell>
            {Object.keys(task.values).map((key, index) => {
              const currentItemDate = new Date(periods[index].startsAt ?? '');
              const isDateWeekend =
                isWeekend(currentItemDate) && periodType === 'day';
              return (
                <TableCell
                  key={`${taskId}-${key}`}
                  align="right"
                  className={cx({
                    [styles.currentColumn]: index === 0,
                    [styles.hide]: isDateWeekend,
                  })}
                >
                  {formatNumber(task.values[key].displayValue, siteLocale)}
                </TableCell>
              );
            })}
          </TableRow>
          {isExpanded &&
            childTasks &&
            renderTasks(childTasks, level + 1, newVisitedIds)}
        </Fragment>
      );
    });
  };

  return (
    <Stack gap={2} {...props}>
      <Stack direction="row" justifyContent="flex-end">
        <ToggleButtonGroup
          value={periodType}
          exclusive
          onChange={(_event, periodView) =>
            periodView !== null ? handleViewChange(periodView) : null
          }
          aria-label="Period view"
          size="small"
        >
          <ToggleButton
            value="day"
            tooltipProps={{ title: t('ViewButtonDay') }}
            data-automation-id={props['data-automation-id'] + 'ViewDayButton'}
          >
            <Today />
          </ToggleButton>
          <ToggleButton
            value="week"
            tooltipProps={{ title: t('ViewButtonWeek') }}
            data-automation-id={props['data-automation-id'] + 'ViewWeekButton'}
          >
            <DateRange />
          </ToggleButton>
          <ToggleButton
            value="month"
            tooltipProps={{ title: t('ViewButtonMonth') }}
            data-automation-id={props['data-automation-id'] + 'ViewMonthButton'}
          >
            <CalendarMonth />
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <Box sx={{ overflowX: 'auto' }}>
        <Table
          sx={{
            borderCollapse: 'separate',
            tableLayout: 'fixed',

            '& .MuiTableCell-root': {
              px: 2,
              py: 1.5,

              '&:first-of-type': {
                paddingLeft: 0.5,
              },
            },
            '& .MuiTableCell-head': {
              fontWeight: typography.fontWeightMedium,
              verticalAlign: 'bottom',
            },
            [`& .MuiTableHead-root .MuiTableRow-root:first-of-type .${styles.currentColumn}`]:
              {
                borderTop: `1px solid ${pink.themeColorPink}`,
                borderRadius: `${borderRadius.borderRadiusLarge} ${borderRadius.borderRadiusLarge} 0 0`,
              },
            '& .MuiTableBody-root .MuiTableRow-root': {
              '&:hover': {
                backgroundColor: grays.colorGray02,
              },
              [`&:last-of-type .${styles.currentColumn}`]: {
                borderBottom: `1px solid ${pink.themeColorPink}`,
                borderRadius: `0 0 ${borderRadius.borderRadiusLarge} ${borderRadius.borderRadiusLarge}`,
              },
              '&:last-of-type': {
                borderBottom: '1px solid green',
              },
            },
          }}
        >
          <colgroup>
            <col style={{ width: 190 }} />
            <col />
            <col style={{ width: 150 }} />
            <col style={{ width: 150 }} />
            <col style={{ width: 120 }} />
            <col style={{ width: 120 }} />
            <col style={{ width: 120 }} />
          </colgroup>

          <TableHead className={styles.tableHead}>
            <TableRow>
              <TableCell colSpan={2}>{t('TableHeaderProject')}</TableCell>
              <TableCell
                align="right"
                className={cx({
                  [styles.currentColumn]: !isEmpty,
                  [styles.hide]: isWeekend(today) && periodType === 'day',
                })}
                data-automation-id={
                  props['data-automation-id'] + 'TableTodayColumn'
                }
              >
                {periodType === 'day' && t('TodayLabel')}
                {periodType === 'week' && t('ThisWeekLabel')}
                {periodType === 'month' && t('ThisMonthLabel')}
              </TableCell>
              <TableCell
                align="right"
                className={cx({
                  [styles.hide]:
                    isWeekend(addDays(today, 1)) && periodType === 'day',
                })}
              >
                {periodType === 'day' &&
                  isSunday(today) &&
                  capitalize(
                    addDays(today, 1).toLocaleString(siteLocale, {
                      weekday: 'short',
                    }),
                  )}

                {periodType === 'day' && !isSunday(today) && t('TomorrowLabel')}
                {periodType === 'week' && t('NextWeekLabel')}
                {periodType === 'month' && t('NextMonthLabel')}
              </TableCell>
              <TableCell
                align="right"
                className={cx({
                  [styles.hide]:
                    isWeekend(addDays(today, 2)) && periodType === 'day',
                })}
              >
                {periodType === 'day' &&
                  capitalize(
                    addDays(today, 2).toLocaleString(siteLocale, {
                      weekday: 'short',
                    }),
                  )}
                {periodType === 'week' &&
                  `${t('WeekLabel')} ${format(addWeeks(today, 2), 'w')}`}
                {periodType === 'month' &&
                  capitalize(
                    addMonths(today, 2).toLocaleString(siteLocale, {
                      month: 'short',
                    }),
                  )}
              </TableCell>
              <TableCell
                align="right"
                className={cx({
                  [styles.hide]:
                    isWeekend(addDays(today, 3)) && periodType === 'day',
                })}
              >
                {periodType === 'day' &&
                  capitalize(
                    addDays(today, 3).toLocaleString(siteLocale, {
                      weekday: 'short',
                    }),
                  )}
                {periodType === 'week' &&
                  `${t('WeekLabel')} ${format(addWeeks(today, 3), 'w')}`}
                {periodType === 'month' &&
                  capitalize(
                    addMonths(today, 3).toLocaleString(siteLocale, {
                      month: 'short',
                    }),
                  )}
              </TableCell>
              <TableCell
                align="right"
                className={cx({
                  [styles.hide]:
                    isWeekend(addDays(today, 4)) && periodType === 'day',
                })}
              >
                {periodType === 'day' &&
                  capitalize(
                    addDays(today, 4).toLocaleString(siteLocale, {
                      weekday: 'short',
                    }),
                  )}
                {periodType === 'week' &&
                  `${t('WeekLabel')} ${format(addWeeks(today, 4), 'w')}`}
                {periodType === 'month' &&
                  capitalize(
                    addMonths(today, 4).toLocaleString(siteLocale, {
                      month: 'short',
                    }),
                  )}
              </TableCell>
              {periodType === 'day' && (
                <>
                  <TableCell
                    align="right"
                    className={cx({
                      [styles.hide]: isWeekend(addDays(today, 5)),
                    })}
                  >
                    {capitalize(
                      addDays(today, 5).toLocaleString(siteLocale, {
                        weekday: 'short',
                      }),
                    )}
                  </TableCell>
                  <TableCell
                    align="right"
                    className={cx({
                      [styles.hide]: isWeekend(addDays(today, 6)),
                    })}
                  >
                    {capitalize(
                      addDays(today, 6).toLocaleString(siteLocale, {
                        weekday: 'short',
                      }),
                    )}
                  </TableCell>
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody
            data-automation-id={props['data-automation-id'] + 'TableBody'}
          >
            {isLoading ||
              (isFetchingChildTasks.size > 0 && (
                <>
                  <PersonalPlanTableSkeleton />
                  <PersonalPlanTableSkeleton />
                  <PersonalPlanTableSkeleton />
                  <PersonalPlanTableSkeleton />
                </>
              ))}
            {isError && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  {t('ErrorText')}
                </TableCell>
              </TableRow>
            )}
            {isSuccess && isEmpty && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Alert severity="info">{t('NoDataText')}</Alert>
                </TableCell>
              </TableRow>
            )}
            {rootTasks &&
              isSuccess &&
              isFetchingChildTasks.size === 0 &&
              renderTasks(rootTasks.model.properties || [])}
          </TableBody>
        </Table>
      </Box>
    </Stack>
  );
};
